import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "@/lib/utils";

const brandButtonVariants = cva(
	"inline-flex items-center justify-center gap-1.5 whitespace-nowrap font-mono text-sm uppercase tracking-[0.14em] transition-[color,background-color,border-color,transform] duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40 font-normal rounded-none",
	{
		variants: {
			variant: {
				primary: "bg-brand text-background hover:bg-brand-hover",
				secondary:
					"bg-card text-foreground border border-border hover:border-brand/40 hover:text-brand",
				outline:
					"bg-background border border-border text-foreground hover:border-brand/40 hover:text-brand",
				ghost: "bg-transparent text-muted-foreground hover:text-brand",
				destructive:
					"bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/40",
			},
			size: {
				default: "min-h-10 px-4",
				sm: "min-h-8 px-3 text-sm",
				lg: "min-h-11 px-5",
			},
		},
		defaultVariants: {
			variant: "primary",
			size: "default",
		},
	},
);

export interface BrandButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof brandButtonVariants> {
	asChild?: boolean;
	ref?: React.Ref<HTMLButtonElement>;
}

function BrandButton({
	className,
	variant,
	size,
	asChild,
	type,
	ref,
	...props
}: BrandButtonProps) {
	const Comp = asChild ? Slot : "button";
	return (
		<Comp
			className={cn(brandButtonVariants({ variant, size, className }))}
			ref={ref}
			{...(asChild ? {} : { type: type ?? "button" })}
			{...props}
		/>
	);
}

export { BrandButton };
